// Pasted HTML (e.g. from signature generators or webmail) can carry images inlined as `data:` URIs.
// Left inline they bypass the upload flow and its size/type checks, blow the request body past the server's
// JSON limit (413) and are dropped by the server-side sanitizer anyway. So we take them out of the HTML and
// hand them back as files, which the editor can route through its regular attachment upload.
const DATA_URI_REGEX = /^data:(?<mimeType>[^;,]*)(?<parameters>(?:;[^;,]*)*),(?<payload>.*)$/is

const isDataUri = (value) => {
  return value?.trim()
    .toLowerCase()
    .startsWith('data:')
}

const convertDataUriToFile = ({ dataUri, fileName }) => {
  const match = dataUri.trim()
    .match(DATA_URI_REGEX)

  if (!match) {
    return undefined
  }

  const { mimeType, parameters, payload } = match.groups

  const isBase64 = parameters.toLowerCase()
    .split(';')
    .includes('base64')

  try {
    const binary = isBase64 ? window.atob(payload.replace(/\s/g, '')) : decodeURIComponent(payload)
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0))
    const type = mimeType.toLowerCase() || 'text/plain'
    const extension = type.split('/')
      .pop()
      .replace('+xml', '')
      .replace('jpeg', 'jpg')

    return new File([ bytes ], `${fileName}.${extension}`, { type })
  } catch {
    return undefined // malformed payload: the image is dropped
  }
}

export default function (html) {
  const template = document.createElement('template') // template content is inert: images inside are not loaded

  template.innerHTML = html

  const files = []

  for (const image of template.content.querySelectorAll('img')) {
    const src = image.getAttribute('src')
    const srcsetCandidates = image.getAttribute('srcset')
      ?.split(',') ?? []

    if (!isDataUri(src) && !srcsetCandidates.some(isDataUri)) {
      continue
    }

    image.remove()

    // an image whose data URI lives only in srcset has no single source to convert, so it is just dropped
    const file = isDataUri(src) && convertDataUriToFile({
      dataUri: src,
      fileName: `pasted-image-${files.length + 1}`,
    })

    if (file) {
      files.push(file)
    }
  }

  return {
    html: template.innerHTML,
    files,
  }
}
